import { useTranslation } from "react-i18next";
import { TellescopeModelFromName, useTellescopeInfiniteQuery } from "@tellescope/tanstack-query";
import { formatDateTime } from "@tellescope/utils";
import { cn } from "@tellescope/design-system";
import { FLOATING_THUMB_CLASS, SCROLL_SURFACE_CLASS, scrollSurfaceStyle } from "@tellescope/react-hooks";
import { useScrollSurface } from "./useScrollSurface";
import { BlockEmptyState } from "../blocks/BlockEmptyState";

export interface FormsSectionProps {
  contact: TellescopeModelFromName<"endusers">;
  onSelectForm?: (form: TellescopeModelFromName<"form_responses">) => void;
}

export const FormsSection = ({ contact, onSelectForm }: FormsSectionProps) => {
  const { t } = useTranslation("contacts");
  const { data: formResponses } = useTellescopeInfiniteQuery({
    model: "form_responses",
    method: "getSome",
    request: {
      filter: {
        enduserId: contact.id,
      },
    },
    select: (data) => data.pages.flat(),
  });

  const formCount = formResponses?.length;
  const { isOverflowing, scrollRef, thumbRef, handleScroll } = useScrollSurface();

  return (
    <div className="relative flex flex-1 min-h-0">
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        style={scrollSurfaceStyle({ faded: isOverflowing })}
        className={cn("flex-1 overflow-y-auto space-y-2 pr-2", SCROLL_SURFACE_CLASS, !formCount && "flex flex-col")}
      >
        {formResponses?.length ? (
          formResponses.map((form) => (
            <div
              key={form.id}
              className="p-3 border rounded-lg text-left hover:shadow-md transition-shadow cursor-pointer bg-white"
              onClick={() => onSelectForm?.(form)}
              data-testid="contact-details-block-item"
            >
              <div className="text-sm font-medium">{form.formTitle}</div>
              <div className="text-xs text-muted-foreground mt-1">
                {formatDateTime(form.submittedAt ?? form.updatedAt)}
              </div>
            </div>
          ))
        ) : (
          <div className="flex flex-1 items-center justify-center">
            <BlockEmptyState>{t("No Form Responses for This Contact")}</BlockEmptyState>
          </div>
        )}
      </div>
      <div ref={thumbRef} className={FLOATING_THUMB_CLASS} />
    </div>
  );
};
